import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC, Key } from 'react';
import { Input, Select, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ipdProjectApi } from '../api/services';
import type { IpdProjectListItemVO, IpdProjectBrandOptionVO, IpdProjectStatusOptionVO } from '../api/types';
import {
  IpcFilterToolbar,
  IpcModal,
  IpcStatusBadge,
  IpcTable,
  IpcTag,
  IPC_FILTER_MULTI_SELECT_PROPS,
  type IpcStatusBadgeVariant,
} from './ui';

const PAGE_SIZE = 20;

export function formatIpdProjectLabel(row: Pick<IpdProjectListItemVO, 'project_code' | 'name'>): string {
  const code = row.project_code?.trim() || '-';
  const name = row.name?.trim();
  return name ? `${code} · ${name}` : code;
}

export function ipdBrandKey(row: Pick<IpdProjectListItemVO, 'brand_code' | 'brand'>): string {
  const code = row.brand_code?.trim();
  if (code) return `code:${code}`;
  const name = row.brand?.trim();
  return name ? `name:${name}` : '';
}

export function sameIpdBrand(
  a: Pick<IpdProjectListItemVO, 'brand_code' | 'brand'>,
  b: Pick<IpdProjectListItemVO, 'brand_code' | 'brand'>,
): boolean {
  const ka = ipdBrandKey(a);
  const kb = ipdBrandKey(b);
  if (!ka || !kb) return false;
  return ka === kb;
}

function ipdStatusVariant(text?: string): IpcStatusBadgeVariant {
  const t = (text ?? '').trim();
  if (t.includes('完成')) return 'success';
  if (t.includes('进行')) return 'primary';
  if (t.includes('待') || t.includes('未开始') || t.includes('未启动')) return 'muted';
  return 'default';
}

type Props = {
  open: boolean;
  selected: IpdProjectListItemVO[];
  /** 打开弹窗时默认选中的品牌筛选（IPC 项目 brand_code） */
  defaultBrand?: string;
  /** 锁定品牌筛选，禁止切换/清空（详情页更换 IPD） */
  lockBrand?: boolean;
  /** 禁止取消勾选的 IPD 项目 id（最新/已发布版本仍有 SKU 引用） */
  lockedIds?: number[];
  title?: string;
  requireSelection?: boolean;
  onCancel: () => void;
  onOk: (rows: IpdProjectListItemVO[]) => void | Promise<void>;
};

export const IpdProjectPickerModal: FC<Props> = ({
  open,
  selected,
  defaultBrand = '',
  lockBrand = false,
  lockedIds = [],
  title = '选择 IPD 项目',
  requireSelection = false,
  onCancel,
  onOk,
}) => {
  const [keywordDraft, setKeywordDraft] = useState('');
  const [brandDraft, setBrandDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState<string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [brand, setBrand] = useState('');
  const [status, setStatus] = useState<string[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<IpdProjectListItemVO[]>([]);
  const [brands, setBrands] = useState<IpdProjectBrandOptionVO[]>([]);
  const [statusOptions, setStatusOptions] = useState<IpdProjectStatusOptionVO[]>([]);
  const [draftSelected, setDraftSelected] = useState<IpdProjectListItemVO[]>([]);
  const [confirming, setConfirming] = useState(false);

  const fetchList = useCallback(async (next?: {
    pageNum?: number;
    pageSize?: number;
    keyword?: string;
    brand?: string;
    status?: string[];
  }) => {
    const pn = next?.pageNum ?? pageNum;
    const ps = next?.pageSize ?? pageSize;
    const kw = next?.keyword ?? keyword;
    const br = next?.brand ?? brand;
    const st = next?.status ?? status;
    setLoading(true);
    try {
      const res = await ipdProjectApi.getList({
        pageNum: pn,
        pageSize: ps,
        keyword: kw || undefined,
        brand: br || undefined,
        status: st.length > 0 ? st.join(',') : undefined,
      });
      setList(res?.list ?? []);
      setTotal(res?.total ?? 0);
      setPageNum(res?.pageNum ?? pn);
      setPageSize(res?.pageSize ?? ps);
      if (res?.brands) setBrands(res.brands);
      if (res?.status_options) setStatusOptions(res.status_options);
    } catch (e) {
      console.error('加载 IPD 项目失败', e);
      message.error(e instanceof Error ? e.message : '加载 IPD 项目失败');
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageNum, pageSize, keyword, brand, status]);

  useEffect(() => {
    if (!open) return;
    const initialBrand = defaultBrand.trim();
    setKeywordDraft('');
    setBrandDraft(initialBrand);
    setStatusDraft([]);
    setKeyword('');
    setBrand(initialBrand);
    setStatus([]);
    setPageNum(1);
    setPageSize(PAGE_SIZE);
    setDraftSelected(selected);
    setConfirming(false);
    void fetchList({ pageNum: 1, pageSize: PAGE_SIZE, keyword: '', brand: initialBrand, status: [] });
    // 仅在弹框打开时同步初值
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commitFilters = useCallback((next: {
    keyword: string;
    brand: string;
    status: string[];
  }, skipIfUnchanged = false) => {
    const nextBrand = lockBrand ? defaultBrand.trim() : next.brand;
    if (
      skipIfUnchanged
      && next.keyword === keyword
      && nextBrand === brand
      && JSON.stringify(next.status) === JSON.stringify(status)
    ) return;
    setKeyword(next.keyword);
    setKeywordDraft(next.keyword);
    setBrand(nextBrand);
    setBrandDraft(nextBrand);
    setStatus(next.status);
    setStatusDraft(next.status);
    setPageNum(1);
    void fetchList({
      pageNum: 1,
      keyword: next.keyword,
      brand: nextBrand,
      status: next.status,
    });
  }, [brand, defaultBrand, fetchList, keyword, lockBrand, status]);

  const applyFilters = useCallback(() => {
    commitFilters({
      keyword: keywordDraft.trim(),
      brand: brandDraft,
      status: statusDraft,
    }, true);
  }, [brandDraft, commitFilters, keywordDraft, statusDraft]);

  const resetFilters = useCallback(() => {
    const initialBrand = defaultBrand.trim();
    setKeywordDraft('');
    setBrandDraft(initialBrand);
    setStatusDraft([]);
    setKeyword('');
    setBrand(initialBrand);
    setStatus([]);
    setPageNum(1);
    void fetchList({ pageNum: 1, keyword: '', brand: initialBrand, status: [] });
  }, [defaultBrand, fetchList]);

  const brandLabel = useCallback((code: string) => {
    const hit = brands.find(b => b.code === code);
    return hit?.name || code;
  }, [brands]);

  const statusLabel = useCallback((value: string) => {
    const hit = statusOptions.find(s => s.value === value);
    return hit?.label || value;
  }, [statusOptions]);

  const appliedChips = [
    keyword.trim() ? { key: 'keyword', label: `搜索: ${keyword.trim()}` } : null,
    brand && !lockBrand ? { key: 'brand', label: `品牌: ${brandLabel(brand)}` } : null,
    ...status.map(value => ({ key: `status:${value}`, label: `状态: ${statusLabel(value)}` })),
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeChip = (key: string) => {
    let nextKeyword = keyword;
    let nextBrand = lockBrand ? defaultBrand.trim() : brand;
    let nextStatus = status;
    if (key === 'keyword') nextKeyword = '';
    else if (key === 'brand') {
      if (lockBrand) return;
      nextBrand = '';
    } else if (key.startsWith('status:')) {
      nextStatus = status.filter(s => s !== key.slice('status:'.length));
    } else return;
    commitFilters({ keyword: nextKeyword, brand: nextBrand, status: nextStatus });
  };

  const lockedIdSet = useMemo(() => new Set(lockedIds.filter(id => id > 0)), [lockedIds]);

  const lockedBrand = lockBrand ? defaultBrand.trim() : '';
  const rowMatchesLockedBrand = (row: IpdProjectListItemVO) => {
    if (!lockedBrand) return true;
    return row.brand_code === lockedBrand || row.brand === lockedBrand;
  };
  const lockedBrandRow = draftSelected[0];

  const warnLockedRemoval = () => {
    message.warning('最新或已发布版本中仍有规格引用该 IPD 的 SKU，请先解除对应规格的 SKU 关联后再移除');
  };

  const columns: ColumnsType<IpdProjectListItemVO> = useMemo(() => [
    {
      title: '项目编码 / 名称',
      key: 'codeName',
      ellipsis: true,
      render: (_, row) => <span className="ipd-picker-code-name">{formatIpdProjectLabel(row)}</span>,
    },
    {
      title: '品牌',
      dataIndex: 'brand',
      width: 120,
      ellipsis: true,
      render: (value: string, row) => value || row.brand_code || '-',
    },
    {
      title: 'NPD',
      dataIndex: 'npd_names',
      width: 140,
      ellipsis: true,
      render: (value: string) => value?.trim() || '-',
    },
    {
      title: '状态',
      dataIndex: 'status_text',
      width: 110,
      render: (value: string) => (
        <IpcStatusBadge variant={ipdStatusVariant(value)}>{value?.trim() || '-'}</IpcStatusBadge>
      ),
    },
    {
      title: '规格数',
      dataIndex: 'spec_count',
      width: 88,
      align: 'center',
      render: (value: number) => value ?? 0,
    },
  ], []);

  const handleConfirm = async () => {
    if (requireSelection && draftSelected.length === 0) {
      message.warning('请选择 IPD 项目');
      return;
    }
    if (lockedBrand && draftSelected.some(r => !rowMatchesLockedBrand(r))) {
      message.warning('请选择当前项目品牌的 IPD 项目');
      return;
    }
    if (lockedIdSet.size > 0) {
      const missingLocked = [...lockedIdSet].filter(id => !draftSelected.some(r => r.id === id));
      if (missingLocked.length > 0) {
        warnLockedRemoval();
        return;
      }
    }
    setConfirming(true);
    try {
      await onOk(draftSelected);
    } catch {
      // 错误由调用方提示，弹窗保持打开
    } finally {
      setConfirming(false);
    }
  };

  return (
    <IpcModal
      title={title}
      open={open}
      onOk={() => { void handleConfirm(); }}
      onCancel={onCancel}
      okText="确定"
      cancelText="取消"
      width={960}
      style={{ maxWidth: '96vw' }}
      confirmLoading={confirming}
      className="ipd-project-picker-modal"
    >
      <IpcFilterToolbar
        onReset={resetFilters}
        onSubmit={applyFilters}
        chipItems={appliedChips}
        onRemoveChip={removeChip}
        onClearChips={resetFilters}
      >
        <Input
          placeholder="项目名称 / 编号搜索..."
          value={keywordDraft}
          onChange={e => setKeywordDraft(e.target.value)}
          onPressEnter={applyFilters}
          allowClear
          className="ipc-filter-bar__field"
        />
        <Select
          placeholder={lockBrand ? '项目品牌' : '全部品牌'}
          value={brandDraft || undefined}
          onChange={v => {
            commitFilters({
              keyword: keywordDraft.trim(),
              brand: lockBrand ? defaultBrand.trim() : (v ?? ''),
              status: statusDraft,
            }, true);
          }}
          options={brands.map(b => ({ value: b.code || b.name, label: b.name || b.code }))}
          allowClear={!lockBrand}
          disabled={lockBrand}
          showSearch
          optionFilterProp="label"
          getPopupContainer={() => document.body}
          className="ipc-filter-bar__field"
        />
        <Select
          mode="multiple"
          placeholder="全部状态"
          value={statusDraft}
          onChange={v => {
            commitFilters({
              keyword: keywordDraft.trim(),
              brand: brandDraft,
              status: v ?? [],
            }, true);
          }}
          options={statusOptions.map(s => ({ value: s.value, label: s.label || s.value }))}
          allowClear
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          className="ipc-filter-bar__field"
        />
      </IpcFilterToolbar>
      {draftSelected.length > 0 && (
        <div className="ipd-picker-selected">
          <span className="ipd-picker-selected__label">已选 {draftSelected.length} 项</span>
          <div className="ipd-picker-selected__tags">
            {draftSelected.map(row => {
              const label = formatIpdProjectLabel(row);
              const isLocked = lockedIdSet.has(row.id);
              return (
                <IpcTag
                  key={row.id}
                  variant="primary"
                  closable={!isLocked}
                  title={isLocked ? `${label}（最新/已发布版本仍有 SKU 引用，不可移除）` : label}
                  onClose={() => {
                    if (isLocked) {
                      warnLockedRemoval();
                      return;
                    }
                    setDraftSelected(prev => prev.filter(r => r.id !== row.id));
                  }}
                >
                  {label}
                </IpcTag>
              );
            })}
          </div>
          <button
            type="button"
            className="ipd-picker-selected__clear"
            onClick={() => {
              const kept = draftSelected.filter(r => lockedIdSet.has(r.id));
              if (kept.length === draftSelected.length && kept.length > 0) {
                warnLockedRemoval();
                return;
              }
              if (kept.length > 0 && kept.length < draftSelected.length) {
                message.warning('已保留仍被最新/已发布版本 SKU 引用的 IPD 项目');
              }
              setDraftSelected(kept);
            }}
          >
            清空
          </button>
        </div>
      )}
      <IpcTable<IpdProjectListItemVO>
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        compact
        frameless
        showHeaderIcon={false}
        selectedCount={draftSelected.length}
        scroll={{ y: 360 }}
        rowSelection={{
          selectedRowKeys: draftSelected.map(r => r.id),
          preserveSelectedRowKeys: true,
          getCheckboxProps: row => ({
            disabled:
              (lockedIdSet.has(row.id) && draftSelected.some(r => r.id === row.id))
              || !rowMatchesLockedBrand(row)
              || (!!lockedBrandRow && !sameIpdBrand(lockedBrandRow, row)),
          }),
          onChange: (_keys: Key[], rows) => {
            const pageIds = new Set(list.map(r => r.id));
            const keptOffPage = draftSelected.filter(r => !pageIds.has(r.id));
            const currentPageSelected = rows.filter((r): r is IpdProjectListItemVO => r != null);
            const droppedLocked = draftSelected.filter(
              r => pageIds.has(r.id) && lockedIdSet.has(r.id) && !currentPageSelected.some(s => s.id === r.id),
            );
            if (droppedLocked.length > 0) {
              warnLockedRemoval();
            }
            const lockedOnPage = draftSelected.filter(r => pageIds.has(r.id) && lockedIdSet.has(r.id));
            const mergedMap = new Map<number, IpdProjectListItemVO>();
            for (const r of [...keptOffPage, ...lockedOnPage, ...currentPageSelected]) {
              mergedMap.set(r.id, r);
            }
            const merged = Array.from(mergedMap.values());
            if (merged.length > 1) {
              const first = merged[0];
              const mixed = merged.some(r => !sameIpdBrand(first, r));
              if (mixed) {
                message.warning('请选择同一品牌的 IPD 项目');
                return;
              }
            }
            setDraftSelected(merged);
          },
        }}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPageNum(p);
            setPageSize(ps);
            void fetchList({ pageNum: p, pageSize: ps });
          },
        }}
        emptyDescription="暂无 IPD 项目"
      />
    </IpcModal>
  );
};
